import { useQuery } from '@tanstack/react-query'
import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type Ref,
} from 'react'
import { Link } from 'react-router'

import {
  emergencyHelpHospitalsKey,
  fetchEmergencyHelpHospitals,
  type EmergencyHelpHospital,
} from '../../api/emergencyHelp'
import { Icon } from '../../components/Icon'
import { distanceKm, formatDistance, type Point } from '../../lib/distance'
import { useGeolocation } from '../../lib/useGeolocation'
import { BackLink, EmergencyHelpShell, Inline112 } from './chrome'
import { useArrivalFocus } from './focus'
import { EMERGENCY_HELP_PATH, hospitalPath } from './paths'

/**
 * Emergency Help, screen 2: find a partner hospital (M35-T06, spec §C.1.2,
 * ADR-0064).
 *
 * THE PATIENT'S LOCATION NEVER LEAVES THE BROWSER. It is read once, on
 * request, and used here to work out a straight-line distance to each
 * hospital's verified coordinates. No request carries it.
 *
 * Distance is offered only when T03 says `distance_available`, i.e. EVERY
 * listed hospital's location is verified: a distance among a mix of real and
 * placeholder positions would mislead. Until then the location button is not
 * shown at all (asking for someone's location to do nothing with it collects
 * it for no purpose), and the list is sorted by name.
 *
 * The text filter matches name and town only: an unverified hospital's
 * address is hidden, so filtering on it would hide unverified hospitals.
 */

const COLLAPSED = 3

/** Open before divert before closed (spec §C.1.2), unknown last. */
const STATUS_ORDER: Record<string, number> = { open: 0, divert: 1, closed: 2 }
const statusRank = (s: string | null) => (s ? (STATUS_ORDER[s] ?? 3) : 3)

type Row = { hospital: EmergencyHelpHospital; km: number | null }

function hasPoint(
  h: EmergencyHelpHospital,
): h is EmergencyHelpHospital & Point {
  return typeof h.lat === 'number' && typeof h.lng === 'number'
}

export function EmergencyHospitals(): ReactNode {
  const query = useQuery({
    queryKey: emergencyHelpHospitalsKey,
    queryFn: ({ signal }) => fetchEmergencyHelpHospitals(signal),
    // One retry, not the default three with backoff: in an emergency the
    // failure, and its "call 112", must show within a second or two.
    retry: 1,
    // Offline, the default pauses the query and "Loading…" never ends; this
    // way the failure, and its 112, shows.
    networkMode: 'always',
  })
  const geo = useGeolocation()
  const [filter, setFilter] = useState('')
  const [showAll, setShowAll] = useState(false)
  const filterId = useId()
  const heading = useArrivalFocus<HTMLHeadingElement>()
  // "Show all" removes itself, so focus moves to the first card it revealed.
  const firstRevealed = useRef<HTMLAnchorElement>(null)
  useEffect(() => {
    if (showAll) firstRevealed.current?.focus()
  }, [showAll])

  const distanceOn = query.data?.distance_available === true
  const here = distanceOn ? geo.coords : null

  const rows: Row[] = useMemo(() => {
    const list = query.data?.data ?? []
    const needle = filter.trim().toLowerCase()
    const matched = needle
      ? list.filter(
          (h) =>
            h.name.toLowerCase().includes(needle) ||
            h.city.toLowerCase().includes(needle),
        )
      : list
    const withKm: Row[] = matched.map((h) => ({
      hospital: h,
      km: here && hasPoint(h) ? distanceKm(here, h) : null,
    }))
    const byName = (a: Row, b: Row) =>
      a.hospital.name.localeCompare(b.hospital.name)
    if (here) {
      return withKm.sort(
        (a, b) =>
          (a.km ?? Infinity) - (b.km ?? Infinity) ||
          statusRank(a.hospital.emergency_status) -
            statusRank(b.hospital.emergency_status) ||
          byName(a, b),
      )
    }
    return withKm.sort(byName)
  }, [query.data, filter, here])

  const total = query.data?.data.length ?? 0
  const filtering = filter.trim() !== ''
  const shown = showAll || filtering ? rows : rows.slice(0, COLLAPSED)
  const filterNote =
    query.isSuccess && filtering && total > 0
      ? rows.length === 0
        ? `No partner hospital matches “${filter.trim()}”.`
        : `${rows.length} of ${total} hospitals match.`
      : ''

  return (
    <EmergencyHelpShell step={2} title="Find a partner hospital">
      <BackLink to={EMERGENCY_HELP_PATH}>Emergency help</BackLink>
      <div>
        <h1
          ref={heading}
          tabIndex={-1}
          className="font-display text-d-xs font-medium tracking-display text-ink-1 focus:outline-none"
        >
          Find a partner hospital
        </h1>
        <p className="mt-1 text-[12.5px] leading-relaxed text-ink-2">
          For active emergencies, call <Inline112 /> first.
        </p>
      </div>

      {distanceOn && (
        <div className="flex flex-col gap-1.5">
          <button
            type="button"
            onClick={geo.request}
            disabled={geo.status === 'prompting'}
            className="inline-flex items-center justify-center gap-2 rounded-r3 border border-line-2 px-3 py-2.5 text-[13px] font-medium text-ink-1 hover:bg-bg-soft disabled:opacity-60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          >
            <Icon name="nav" size={14} />
            {geo.status === 'granted'
              ? 'Sorted by distance from you'
              : geo.status === 'prompting'
                ? 'Finding your location…'
                : 'Use my current location'}
          </button>
          <p aria-live="polite" className="text-[12px] text-ink-3">
            {geo.status === 'denied' || geo.status === 'unavailable'
              ? 'Your location could not be used. Hospitals are sorted by name.'
              : geo.status === 'granted'
                ? 'Your location stays on this device. It is not sent to Medcor.'
                : ''}
          </p>
        </div>
      )}

      <div className="flex flex-col gap-1.5">
        <label
          htmlFor={filterId}
          className="text-[13px] font-medium text-ink-1"
        >
          Filter by hospital name or town
        </label>
        <input
          id={filterId}
          type="search"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          autoComplete="off"
          className="w-full rounded-r3 border border-line-1 bg-bg-app px-3 py-2.5 text-[14px] text-ink-1 placeholder:text-ink-3 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        />
      </div>

      {!distanceOn && query.isSuccess && (
        <p className="text-[12px] text-ink-3">
          Distances are not available yet. Hospitals are sorted by name.
        </p>
      )}

      {query.isPending && (
        <p role="status" className="text-[13px] text-ink-3">
          Loading partner hospitals…
        </p>
      )}

      {query.isError && (
        <div
          role="alert"
          className="rounded-r3 border border-line-1 bg-bg-soft px-3 py-3 text-[13px] text-ink-1"
        >
          <p>
            Could not load the partner hospitals. In an emergency, call{' '}
            <Inline112 />.
          </p>
          <button
            type="button"
            onClick={() => void query.refetch()}
            className="mt-2 rounded-r3 border border-line-2 px-3 py-1.5 text-[12.5px] font-medium hover:bg-bg-app focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          >
            Try again
          </button>
        </div>
      )}

      {query.isSuccess && total === 0 && (
        <p className="text-[13px] text-ink-2">
          No partner hospitals are listed right now. In an emergency, call{' '}
          <Inline112 />.
        </p>
      )}

      {/* Announced as the filter narrows the list, so a screen-reader user
          knows what the typing did. */}
      {/* Always in the accessibility tree (sr-only when empty, never
          display:none), or a screen reader can miss the first announcement. */}
      <p
        aria-live="polite"
        className={filterNote ? 'text-[12px] text-ink-3' : 'sr-only'}
      >
        {filterNote}
      </p>

      {shown.length > 0 && (
        <ul className="flex flex-col gap-2" aria-label="Partner hospitals">
          {shown.map(({ hospital: h, km }, i) => (
            <li key={h.slug}>
              <HospitalCard
                hospital={h}
                km={km}
                linkRef={i === COLLAPSED ? firstRevealed : undefined}
              />
            </li>
          ))}
        </ul>
      )}

      {!showAll && !filtering && rows.length > COLLAPSED && (
        <button
          type="button"
          onClick={() => setShowAll(true)}
          className="self-start rounded text-[13px] font-semibold text-accent underline underline-offset-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        >
          Show all {rows.length} hospitals
        </button>
      )}

      <p className="border-t border-line-1 pt-3 text-[12px] leading-relaxed text-ink-3">
        Distance does not establish clinical suitability. For active
        emergencies, call <Inline112 />.
      </p>
    </EmergencyHelpShell>
  )
}

function HospitalCard({
  hospital: h,
  km,
  linkRef,
}: {
  hospital: EmergencyHelpHospital
  km: number | null
  linkRef?: Ref<HTMLAnchorElement> | undefined
}): ReactNode {
  return (
    <Link
      ref={linkRef}
      to={hospitalPath(h.slug)}
      className="flex items-center gap-3 rounded-r3 border border-line-1 bg-bg-app px-3 py-3 hover:bg-bg-soft focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
    >
      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="text-[14px] font-semibold text-ink-1">{h.name}</span>
        <span className="text-[12px] text-ink-3">
          {h.city}
          {km !== null && ` · ${formatDistance(km)} away (straight line)`}
        </span>
        <span className="flex flex-wrap gap-1.5">
          <span className="rounded-r1 bg-accent-bg px-1.5 py-0.5 text-[10.5px] font-medium text-accent">
            AMTC partner
          </span>
          {!h.emergency_info_verified && (
            <span className="rounded-r1 bg-bg-soft px-1.5 py-0.5 text-[10.5px] font-medium text-ink-2">
              verify directory data
            </span>
          )}
        </span>
      </span>
      <Icon name="chevr" size={16} className="shrink-0 text-ink-3" />
    </Link>
  )
}
